import { useRef, useState } from 'react';

import { type ResolvableModalFC } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

export interface RegionOption {
  value: string;
  label: string;
  description: string;
  mcpUrl: string;
}

interface RegionSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resolve: (mcpUrl: string | null) => void;
  title: string;
  description: string;
  options: readonly RegionOption[];
}

export const RegionSelectModal: ResolvableModalFC<
  RegionSelectModalProps,
  string | null
> = ({ open, onOpenChange, resolve, title, description, options }) => {
  const [selected, setSelected] = useState<RegionOption>(options[0]);
  const hasResolved = useRef(false);

  const resolveParent = (value: string | null) => {
    hasResolved.current = true;
    resolve(value);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !hasResolved.current) {
      resolveParent(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="max-w-md">
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
        </Dialog.Header>

        <YStack className="gap-2 py-3 max-h-72 overflow-y-auto">
          {options.map((option) => {
            const isSelected = selected.value === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setSelected(option)}
                className={cn(
                  'flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                  isSelected
                    ? 'border-foreground bg-surface-subtle'
                    : 'border-border hover:bg-surface-subtle/60'
                )}
              >
                <span
                  className={cn(
                    'mt-1 size-3.5 shrink-0 rounded-full border',
                    isSelected
                      ? 'border-foreground bg-foreground'
                      : 'border-border'
                  )}
                />
                <YStack className="min-w-0 gap-0.5">
                  <span className="text-sm font-medium">{option.label}</span>
                  <span className="text-xs text-foreground-muted">
                    {option.description}
                  </span>
                </YStack>
              </button>
            );
          })}
        </YStack>

        <Dialog.Footer className="gap-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resolveParent(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              resolveParent(selected.mcpUrl);
            }}
          >
            Continue
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
